import { Link, useMatchRoute, useRouterState } from '@tanstack/react-router';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  SidebarLanguageToggle,
  useSidebar,
  useFeatureFlags,
  useUserType,
} from '@org/ui';
import type { FeatureFlagKey } from '@org/model';
import { useTranslation } from 'react-i18next';
import { useAccess } from './access/use-access';
import { isLockedPath } from '../lib/access';
import {
  Lock,
  LayoutDashboard,
  Map,
  BrickWall,
  ShoppingBag,
  BookOpen,
  CalendarDays,
  PlayCircle,
  Compass,
  SpellCheck,
  MessageSquareText,
  Mic,
  Trophy,
  type LucideIcon,
} from 'lucide-react';

const navItems: Array<{
  labelKey: string;
  href: string;
  icon: LucideIcon;
  featureFlag?: FeatureFlagKey;
  /**
   * Woman-cohort-only entries (achievements wall). Hidden for b2b students — and
   * while the cohort is still resolving, so b2b users never see them flash in.
   */
  hideForB2b?: boolean;
  /**
   * Label shown to b2b students instead of `labelKey` (e.g. the neutral
   * "Central E2E"). Entries with one are hidden while the cohort is still
   * resolving, so neither cohort sees the other's label flash in.
   */
  b2bLabelKey?: string;
}> = [
  { labelKey: 'p2e.sidebar.navigation.dashboard', href: '/', icon: LayoutDashboard },
  { labelKey: 'p2e.sidebar.navigation.journey', href: '/journey', icon: Map, featureFlag: 'journey' },
  // The mural is the zoom-out of the journey, so it sits directly under it.
  { labelKey: 'p2e:mural.navLabel', href: '/mural', icon: BrickWall, featureFlag: 'journey' },
  // Standalone (paid/granted) journeys — the store sits right after the wall.
  { labelKey: 'p2e:products.navLabel', href: '/products', icon: ShoppingBag, featureFlag: 'journey-products' },
  { labelKey: 'p2e.sidebar.navigation.classes', href: '/classes', icon: BookOpen },
  // Free speaking practice with the school's AI teacher (her decision: every student except In Company).
  { labelKey: 'p2e:mellinai.navLabel', href: '/mellinai', icon: Mic, hideForB2b: true, featureFlag: 'mellinai' },
  // Calendar strings live in the p2e namespace; the explicit prefix keeps the
  // rest of this file on the legacy admin-namespace keys.
  { labelKey: 'p2e:calendar.navLabel', href: '/calendar', icon: CalendarDays },
  { labelKey: 'p2e.sidebar.navigation.onboard', href: '/onboard', icon: PlayCircle },
  { labelKey: 'p2e.sidebar.navigation.central', href: '/central', icon: Compass, b2bLabelKey: 'p2e:centralB2b.navLabel' },
  { labelKey: 'p2e:achievements.navLabel', href: '/conquistas', icon: Trophy, hideForB2b: true },
  { labelKey: 'p2e.sidebar.navigation.grammarAid', href: '/grammar-aid', icon: SpellCheck },
  { labelKey: 'p2e.sidebar.navigation.feedback', href: '/feedback', icon: MessageSquareText },
  // { labelKey: 'p2e.sidebar.navigation.events', href: '/events', icon: CalendarDays },
];

export function AppSidebar() {
  const matchRoute = useMatchRoute();
  const routerState = useRouterState();
  const { isMobile, setOpen, setOpenMobile, toggleSidebar } = useSidebar();
  const { t } = useTranslation('admin');
  const { isEnabled: isFlagEnabled } = useFeatureFlags();
  const { isB2b, isLoading: isUserTypeLoading } = useUserType();
  // Limited mode (trial over, nothing paid): the learning pages still open
  // (faded, see LimitedVeil); the sidebar only marks them with a lock glyph.
  const { data: access } = useAccess();
  const limited = access?.limited ?? false;
  const visibleItems = navItems.filter(
    (item) =>
      (!item.featureFlag || isFlagEnabled(item.featureFlag)) &&
      (!item.hideForB2b || (!isB2b && !isUserTypeLoading)) &&
      (!item.b2bLabelKey || !isUserTypeLoading),
  );

  const closeSidebar = () => {
    if (isMobile) {
      setOpenMobile(false);
    } else {
      setOpen(false);
    }
  };

  return (
    <Sidebar className="border-r border-rose-100 bg-white/90" collapsible="icon">
      <SidebarHeader className="p-4 group-data-[collapsible=icon]:justify-center">
        <div className="flex items-center gap-2.5 group-data-[collapsible=icon]:justify-center">
          <button
            type="button"
            onClick={toggleSidebar}
            aria-label={t('common.nav.menu')}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#cf2e2e] shadow-sm"
          >
            <img src="/icon-hand-small-white.png" alt="" aria-hidden="true" className="h-5 w-5 object-contain" />
          </button>
          <span className="text-base font-semibold text-gray-900 group-data-[collapsible=icon]:hidden">English2Empower</span>
        </div>
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>{t('common.nav.menu')}</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {visibleItems.map((item) => {
                const isActive =
                  matchRoute({ to: item.href, fuzzy: item.href === '/' }) &&
                  routerState.location.pathname.startsWith(item.href);
                const Icon = item.icon;
                const label = t(isB2b && item.b2bLabelKey ? item.b2bLabelKey : item.labelKey);
                const locked = limited && isLockedPath(item.href);
                return (
                  <SidebarMenuItem key={item.href}>
                    <SidebarMenuButton asChild isActive={!!isActive} tooltip={label} className="rounded-lg">
                      <Link to={item.href} onClick={closeSidebar} data-locked={locked ? 'true' : undefined}>
                        {locked ? <Lock /> : <Icon />}
                        <span>{label}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter className="p-3 group-data-[collapsible=icon]:hidden">
        <SidebarLanguageToggle />
      </SidebarFooter>
      <SidebarRail className="hidden md:flex" />
    </Sidebar>
  );
}